/**
 * Right-edge vertical icon rail (analysis tool launcher) that overlays the map.
 *
 * Replaces the old hamburger gate in `MapView`/`LayerTogglePanel`. Each icon represents one tool;
 * the parent (HomePage) renders the actual content panel as a floating card to the left of the
 * rail by reading the `activeIcon` state.
 *
 * Responsive behavior:
 *   - `sm+` (≥640px): always-visible 2-column grid (4 rows × 2 cols) to keep the rail compact;
 *     each cell is icon + label, same styling as before.
 *   - `< sm` (phones): collapsed to a single launcher button. Tapping it opens a bottom-sheet
 *     with the same icons in a 2-column card grid (same 6rem card size as desktop).
 *
 * Icon set on the rail:
 *   - 'farm' is pinned first when any field-level analysis entitlement is assigned.
 *   - The 7 large-scale tab icons (`LAYER_TOGGLE_PANEL_TAB_ORDER`) follow when unlocked.
 *
 * Entitlements: large-scale icons are filtered by `isPanelTabUnlocked(allowedEntitlementSet, …)`.
 * The 'farm' icon is shown whenever the user has any field-level analysis entitlement; gating of
 * its content (heatmap, time series, etc.) is handled inside `AnalysisPanel` itself.
 *
 * Visibility: shown by default; parent can persist `visible` + `onVisibleChange` so users may hide
 * the rail and restore it via the round launcher (eye-off control when expanded).
 */
import { useState, type CSSProperties } from 'react';
import { useTheme } from '../theme/ThemeContext';
import {
  getMapRailTheme,
  mapRailCellHoverClass,
  mapRailFarmDotRingClass,
  mapRailHideButtonClass,
  mapRailLabelColor,
  mapRailLauncherNotifyRingClass,
  mapRailSheetIconButtonClass,
  mapRailShellClass,
  mapRailToolCardClass,
} from '../theme/themeStyles';
import {
  LAYER_TOGGLE_PANEL_TAB_ORDER,
  isFarmAnalysisNavUnlocked,
  isPanelTabUnlocked,
  type LayerTogglePanelTabId,
} from '../../../config/analysisLayerEntitlements';
import yieldIcon from '../../../assets/icons/yield.svg';
import temporalIcon from '../../../assets/icons/temporal.svg';
import droneIcon from '../../../assets/icons/drone.svg';
import cropTypeIcon from '../../../assets/icons/crop-type.svg';
import lossDamageIcon from '../../../assets/icons/l&d.svg';
import carbonIcon from '../../../assets/icons/carbon.svg';
import detectionIcon from '../../../assets/icons/detection.svg';

export type MapToolRailIcon = 'farm' | LayerTogglePanelTabId;

/** Semi-transparent gray circle behind each rail icon so custom assets read clearly. */
export const RAIL_ICON_CIRCLE_BG = '#D9D9D9E5';

/** Brand green for the currently selected analysis tool (border, label, highlights). */
export const RAIL_LAYER_ACTIVE_COLOR = '#008540';

/** Soft tint used behind the active rail row / mobile grid cell. */
export const RAIL_LAYER_ACTIVE_BG = '#00854014';

/** CSS filter that turns embedded PNG/SVG asset icons white (used on the active green/gold circle). */
export const RAIL_ICON_ACTIVE_FILTER = 'brightness(0) invert(1)';

/** CSS filter that turns asset icons solid black (gold nav pill + dark text). */
export const RAIL_ICON_INK_BLACK_FILTER = 'brightness(0)';

/** Light-mode inactive asset icons — PNGs are light-on-dark; darken to brand green for contrast. */
export const RAIL_ICON_INACTIVE_LIGHT_FILTER =
  'brightness(0) saturate(100%) invert(28%) sepia(22%) saturate(1200%) hue-rotate(118deg) brightness(95%) contrast(92%)';

/** Dark-mode inactive asset icons — soft white (`#e6edf3`-ish) to match the Digisaka dark palette. */
export const RAIL_ICON_INACTIVE_DARK_FILTER = 'brightness(0) invert(0.92)';

export function getRailIconAssetFilter(
  active: boolean,
  theme: 'dark' | 'light',
  ink: 'auto' | 'black' = 'auto',
): string | undefined {
  if (ink === 'black') return RAIL_ICON_INK_BLACK_FILTER;
  if (active) return RAIL_ICON_ACTIVE_FILTER;
  if (theme === 'light') return RAIL_ICON_INACTIVE_LIGHT_FILTER;
  return RAIL_ICON_INACTIVE_DARK_FILTER;
}

/** Circle puck styles for inactive (gray) vs active (solid brand green + white icon). */
export function getRailIconCircleStyle(
  isActive: boolean,
  isFarm = false,
  theme: 'dark' | 'light' = 'light',
): CSSProperties {
  if (isActive) {
    return {
      backgroundColor: 'rgba(255,255,255,0.16)',
      borderColor: 'transparent',
      color: '#FFFFFF',
    };
  }

  if (theme === 'dark') {
    return {
      backgroundColor: 'rgba(52,211,153,0.16)',
      borderColor: 'transparent',
      color: '#34d399',
    };
  }

  return {
    backgroundColor: RAIL_LAYER_ACTIVE_BG,
    borderColor: isFarm ? `${RAIL_LAYER_ACTIVE_COLOR}99` : `${RAIL_LAYER_ACTIVE_COLOR}33`,
    color: isFarm ? RAIL_LAYER_ACTIVE_COLOR : '#374151',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.85)',
  };
}

/** Width of one desktop rail grid cell — keep in sync with `analysisPanelLayout`. */
export const RAIL_DESKTOP_CELL_WIDTH_REM = 5.625;

/** Collapsed launcher (`h-11 w-11`) — keep in sync with `analysisPanelLayout`. */
export const ANALYSIS_TOOLS_LAUNCHER_SIZE_PX = 44;

export type RailTabMeta = {
  /** Custom SVG/PNG asset from `src/assets/icons`. Preferred when available. */
  iconSrc?: string;
  /** Remix Icon class — used as a fallback when no asset exists (e.g. Farm Analysis). */
  iconClass?: string;
  /** Full tool name shown under the icon. */
  fullLabel: string;
  /** Optional extra detail for native `title` on hover (e.g. farm sub-tools). */
  title?: string;
};

interface MapToolRailProps {
  analysisMode: 'field-level' | 'large-scale';
  activeIcon: MapToolRailIcon | null;
  onIconClick: (icon: MapToolRailIcon) => void;
  /** GET /auth/me entitlement set; `null` means superuser/legacy (everything visible). */
  allowedEntitlementSet?: Set<string> | null;
  /** Hide entirely (e.g. while data is loading). Defaults to false. */
  hidden?: boolean;
  /** User preference: show the tool rail (default true). Persisted by the parent. */
  visible?: boolean;
  /** Called when the user hides or restores the rail from the map UI. */
  onVisibleChange?: (visible: boolean) => void;
  /** Override class names of the outer wrapper (positioning is owned by the parent). */
  className?: string;
}

/**
 * Icons + full labels. Kept in sync with `LayerTogglePanel` tab names.
 */
export const RAIL_TAB_META: Record<MapToolRailIcon, RailTabMeta> = {
  farm: {
    iconClass: 'ri-focus-3-line',
    fullLabel: 'Farm analysis',
    title: 'Index map, time series, crop calendar, climate, rice forecast, field report',
  },
  forecast: { iconSrc: yieldIcon, fullLabel: 'Rice yield\nforecast' },
  multiTemporal: { iconSrc: temporalIcon, fullLabel: 'Multi-temporal\nmaps' },
  droneData: { iconSrc: droneIcon, fullLabel: 'Drone' },
  fieldServices: {
    iconClass: 'ri-base-station-line',
    fullLabel: 'Field\nservices',
    title: 'Drone processing, sample imagery, and sensor requests',
  },
  cropArea: { iconSrc: cropTypeIcon, fullLabel: 'Crop type\nmaps' },
  disasterLossDamage: { iconSrc: lossDamageIcon, fullLabel: 'Disaster loss\nand damages' },
  lowCarbon: { iconSrc: carbonIcon, fullLabel: 'Low carbon\nrice' },
  treeDetection: {
    iconSrc: detectionIcon,
    fullLabel: 'AI Detection:\nTrees, Crowns & Fields',
    title: 'AI-based tree detection, crown segmentation, and field boundary delineation',
  },
};

/** Renders either a custom asset icon or a Remix Icon fallback for a rail tool. */
export function RailToolIcon({
  meta,
  active = false,
  theme = 'light',
  ink = 'auto',
  imgClassName = 'h-4 w-4 object-contain',
  iconClassName = 'text-base',
  className = '',
}: {
  meta: RailTabMeta;
  /** When true, asset icons are filtered to white for display on the active green circle. */
  active?: boolean;
  theme?: 'dark' | 'light';
  /** Force asset/Remix icons to black (gold top-nav selected pill). */
  ink?: 'auto' | 'black';
  imgClassName?: string;
  iconClassName?: string;
  className?: string;
}) {
  const assetFilter = getRailIconAssetFilter(active, theme, ink);

  if (meta.iconSrc) {
    return (
      <img
        src={meta.iconSrc}
        alt=""
        aria-hidden
        draggable={false}
        className={`${imgClassName} ${className}`.trim()}
        style={assetFilter ? { filter: assetFilter } : undefined}
      />
    );
  }
  return (
    <i
      className={`${meta.iconClass ?? 'ri-apps-2-line'} ${iconClassName} ${active && ink !== 'black' ? 'text-white' : ''} ${className}`.trim()}
      style={
        ink === 'black'
          ? { color: '#111827' }
          : !active && theme === 'light'
            ? { color: RAIL_LAYER_ACTIVE_COLOR }
            : undefined
      }
      aria-hidden
    />
  );
}

/** Farm first (when any farm tool is assigned), then entitlement-filtered large-scale tools. */
export function getVisibleAnalysisNavIcons(
  allowedEntitlementSet: Set<string> | null = null,
): MapToolRailIcon[] {
  const largeScaleIcons: LayerTogglePanelTabId[] = LAYER_TOGGLE_PANEL_TAB_ORDER.filter((tab) =>
    isPanelTabUnlocked(allowedEntitlementSet, tab),
  );
  const fieldServicesNav = isPanelTabUnlocked(allowedEntitlementSet, 'fieldServices');
  const icons = fieldServicesNav
    ? largeScaleIcons.filter((tab) => tab !== 'droneData')
    : largeScaleIcons;
  if (isFarmAnalysisNavUnlocked(allowedEntitlementSet)) {
    return ['farm', ...icons];
  }
  return icons;
}

export default function MapToolRail({
  analysisMode: _analysisMode,
  activeIcon,
  onIconClick,
  allowedEntitlementSet = null,
  hidden = false,
  visible = true,
  onVisibleChange,
  className = '',
}: MapToolRailProps) {
  if (hidden) return null;

  const icons = getVisibleAnalysisNavIcons(allowedEntitlementSet);

  if (icons.length === 0) return null;

  return (
    <MapToolRailInner
      icons={icons}
      activeIcon={activeIcon}
      onIconClick={onIconClick}
      visible={visible}
      onVisibleChange={onVisibleChange}
      className={className}
    />
  );
}

interface MapToolRailInnerProps {
  icons: MapToolRailIcon[];
  activeIcon: MapToolRailIcon | null;
  onIconClick: (icon: MapToolRailIcon) => void;
  visible: boolean;
  onVisibleChange?: (visible: boolean) => void;
  className: string;
}

/** Round launcher when the analysis tools rail is hidden. */
function AnalysisToolsRailLauncher({
  activeMeta,
  theme,
  onClick,
  className = '',
}: {
  activeMeta: RailTabMeta | null;
  theme: 'dark' | 'light';
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Show analysis tools"
      aria-label="Show analysis tools"
      data-tour-id="map-rail"
      className={`relative flex h-11 w-11 items-center justify-center rounded-full border-2 border-transparent transition-all duration-200 shadow-lg hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008540CC] ${className}`}
      style={getRailIconCircleStyle(Boolean(activeMeta), false, theme)}
    >
      <RailToolIcon
        meta={activeMeta ?? { iconClass: 'ri-apps-2-line', fullLabel: 'Analysis tools' }}
        active={Boolean(activeMeta)}
        theme={theme}
        imgClassName="h-5 w-5 object-contain"
        iconClassName="text-xl"
      />
      <span className="sr-only">Analysis tools</span>
      {!activeMeta && (
        <span
          className={`absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ${mapRailLauncherNotifyRingClass(theme)}`}
          style={{ backgroundColor: RAIL_LAYER_ACTIVE_COLOR }}
          aria-hidden
        />
      )}
    </button>
  );
}

function RailHideButton({
  theme,
  onHide,
  overlay = false,
  compact = false,
  className = '',
}: {
  theme: 'dark' | 'light';
  onHide: () => void;
  /** Match colors to the rail shell when overlaid on the grid. */
  overlay?: boolean;
  compact?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onHide}
      title="Hide analysis tools"
      aria-label="Hide analysis tools"
      className={[mapRailHideButtonClass(theme, { overlay, compact }), className].join(' ')}
    >
      <i className="ri-eye-off-line" aria-hidden />
    </button>
  );
}

/** Icon + label block centered inside the tool card rectangle. */
function RailToolCardContent({
  meta,
  isActive,
  isFarm,
  theme,
  compact = true,
}: {
  meta: RailTabMeta;
  isActive: boolean;
  isFarm: boolean;
  theme: 'dark' | 'light';
  compact?: boolean;
}) {
  const isDark = theme === 'dark';
  const rail = getMapRailTheme(isDark);

  return (
    <span className={rail.toolCardInner}>
      <span
        style={getRailIconCircleStyle(isActive, isFarm, theme)}
        className={`relative flex shrink-0 items-center justify-center rounded-[9px] border-0 transition-colors duration-150 ${
          compact ? rail.iconCircleCompact : rail.iconCircleDefault
        }`}
      >
        <RailToolIcon
          meta={meta}
          active={isActive}
          theme={theme}
          imgClassName={compact ? 'h-[18px] w-[18px] object-contain' : 'h-7 w-7 object-contain'}
          iconClassName={compact ? 'text-base' : 'text-2xl'}
        />
        {isFarm && !isActive && theme === 'light' && (
          <span
            className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ${mapRailFarmDotRingClass(theme, isActive)}`}
            style={{ backgroundColor: RAIL_LAYER_ACTIVE_COLOR }}
            aria-hidden
          />
        )}
      </span>
      <span
        className={`${rail.label} whitespace-pre-line ${compact ? rail.labelCompact : rail.labelDefault}`}
        style={{ color: mapRailLabelColor(theme, isActive, isFarm) }}
      >
        {meta.fullLabel}
      </span>
    </span>
  );
}

function RailToolButton({
  id,
  meta,
  isActive,
  onClick,
  theme,
}: {
  id: MapToolRailIcon;
  meta: RailTabMeta;
  isActive: boolean;
  onClick: () => void;
  theme: 'dark' | 'light';
}) {
  const isFarm = id === 'farm';
  const isDark = theme === 'dark';
  const rail = getMapRailTheme(isDark);
  const nativeTitle = meta.title ? `${meta.fullLabel} — ${meta.title}` : meta.fullLabel;

  return (
    <button
      type="button"
      onClick={onClick}
      title={nativeTitle}
      aria-label={meta.fullLabel}
      aria-pressed={isActive}
      className={[
        rail.toolCard,
        mapRailToolCardClass(theme, isActive, isFarm),
        mapRailCellHoverClass(theme),
        'justify-self-center',
      ].join(' ')}
    >
      <RailToolCardContent meta={meta} isActive={isActive} isFarm={isFarm} theme={theme} />
    </button>
  );
}

function MapToolRailInner({
  icons,
  activeIcon,
  onIconClick,
  visible,
  onVisibleChange,
  className,
}: MapToolRailInnerProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const rail = getMapRailTheme(isDark);

  const handleMobilePick = (id: MapToolRailIcon) => {
    onIconClick(id);
    setSheetOpen(false);
  };

  const activeMeta = activeIcon ? RAIL_TAB_META[activeIcon] : null;

  const hideRail = () => {
    setSheetOpen(false);
    onVisibleChange?.(false);
  };

  const showRail = () => onVisibleChange?.(true);

  if (!visible) {
    return (
      <div className={className}>
        <AnalysisToolsRailLauncher activeMeta={activeMeta} theme={theme} onClick={showRail} />
      </div>
    );
  }

  return (
    <>
      {/* Phones (< sm): collapsed single launcher button */}
      <div className={`sm:hidden relative ${className}`}>
        {onVisibleChange && (
          <RailHideButton
            theme={theme}
            onHide={hideRail}
            compact
            className="absolute -top-1 -right-1 z-10"
          />
        )}
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          title="Open analysis tools"
          aria-label="Open analysis tools"
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          data-tour-id="map-rail"
          className="relative flex h-11 w-11 items-center justify-center rounded-full border-2 border-transparent transition-all duration-200 shadow-lg hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008540CC]"
          style={getRailIconCircleStyle(Boolean(activeMeta), false, theme)}
        >
          <RailToolIcon
            meta={activeMeta ?? { iconClass: 'ri-apps-2-line', fullLabel: 'Analysis tools' }}
            active={Boolean(activeMeta)}
            theme={theme}
            imgClassName="h-5 w-5 object-contain"
            iconClassName="text-xl"
          />
          <span className="sr-only">Analysis tools</span>
          {!activeMeta && (
            <span
              className={`absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ${mapRailLauncherNotifyRingClass(theme)}`}
              style={{ backgroundColor: RAIL_LAYER_ACTIVE_COLOR }}
              aria-hidden
            />
          )}
        </button>
      </div>

      {/* Phones (< sm): bottom sheet listing all tools */}
      {sheetOpen && (
        <div className="sm:hidden fixed inset-0 z-[75] flex items-end" role="dialog" aria-modal="true" aria-label="Analysis tools">
          <button
            type="button"
            className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
            onClick={() => setSheetOpen(false)}
            aria-label="Close analysis tools"
          />
          <div
            className={`relative w-full rounded-t-2xl shadow-2xl pb-[env(safe-area-inset-bottom,0px)] max-h-[75vh] overflow-y-auto ${
              isDark ? rail.mobileSheetDark : rail.mobileSheetLight
            }`}
            style={{ animation: 'rail-sheet-up 220ms ease-out both' }}
          >
            <div className="flex items-center justify-center pt-2 pb-1">
              <div
                className={`h-1 w-10 rounded-full ${isDark ? rail.sheetHandleDark : rail.sheetHandleLight}`}
                aria-hidden
              />
            </div>
            <div className="flex items-center justify-between px-4 pb-2">
              <h3 className={`text-sm font-semibold ${isDark ? rail.sheetTitleDark : rail.sheetTitleLight}`}>
                Analysis tools
              </h3>
              <div className="flex items-center gap-1">
                {onVisibleChange && (
                  <button
                    type="button"
                    onClick={hideRail}
                    title="Hide analysis tools"
                    aria-label="Hide analysis tools"
                    className={mapRailSheetIconButtonClass(theme)}
                  >
                    <i className="ri-eye-off-line text-lg" aria-hidden />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  className={mapRailSheetIconButtonClass(theme)}
                  aria-label="Close"
                >
                  <i className="ri-close-line text-lg" aria-hidden />
                </button>
              </div>
            </div>
            <div className={rail.mobileGrid}>
              {icons.map((id, index) => {
                const meta = RAIL_TAB_META[id];
                const isActive = activeIcon === id;
                const isFarm = id === 'farm';
                const isLastOdd = icons.length % 2 === 1 && index === icons.length - 1;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleMobilePick(id)}
                    title={meta.title ? `${meta.fullLabel} — ${meta.title}` : meta.fullLabel}
                    aria-label={meta.fullLabel}
                    aria-pressed={isActive}
                    className={[
                      rail.toolCard,
                      mapRailToolCardClass(theme, isActive, isFarm),
                      isLastOdd ? 'col-span-2 justify-self-center' : 'justify-self-center',
                      mapRailCellHoverClass(theme),
                    ].join(' ')}
                  >
                    <RailToolCardContent meta={meta} isActive={isActive} isFarm={isFarm} theme={theme} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Tablet/desktop (sm+): 2-column grid — hide control overlays top-right (no Y shift). */}
      <div
        className={`${mapRailShellClass(theme)} relative ${className}`}
        role="toolbar"
        aria-label="Map analysis tools"
        data-tour-id="map-rail"
      >
        {onVisibleChange && (
          <RailHideButton
            theme={theme}
            onHide={hideRail}
            overlay
            compact
            className="absolute top-1 right-1 z-10"
          />
        )}
        {icons.map((id) => {
          const meta = RAIL_TAB_META[id];
          const isActive = activeIcon === id;
          return (
            <RailToolButton
              key={id}
              id={id}
              meta={meta}
              isActive={isActive}
              onClick={() => onIconClick(id)}
              theme={theme}
            />
          );
        })}
      </div>
    </>
  );
}
